import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { JTLDescription } from '@/lib/api/descriptions'
import type { ContentFieldKey } from './channel-constants'
import { MULTILINE_FIELDS, LANGUAGE_LABELS, FIELD_LABELS } from './channel-constants'
import { ChannelEditorHeader } from './ChannelEditorHeader'
import { ChannelEditorField } from './ChannelEditorField'
import { ChannelEditorSeoSection } from './ChannelEditorSeoSection'
import { ChannelTranslateColumn } from './ChannelTranslateColumn'
import { SuggestionFieldColumn } from './SuggestionFieldColumn'

const MAIN_FIELDS: ContentFieldKey[] = ['Name', 'ShortDescription', 'Description']
const SEO_FIELDS: ContentFieldKey[] = ['SeoTitleTag', 'SeoMetaDescription', 'SeoMetaKeywords', 'SeoPath']

interface SuggestionMap {
  fields: Partial<Record<ContentFieldKey, string>>
  onApplyField: (key: ContentFieldKey) => void
}

interface ChannelEditorPanelProps {
  productId: string
  channelId: string
  channelName: string
  description: JTLDescription | undefined
  masterDescription: JTLDescription | undefined
  isMaster: boolean
  translateMode: boolean
  translateSourceDesc: JTLDescription | undefined
  translateSourceLang: string
  targetLang: string
  availableLanguages: string[]
  onTargetLangChange: (lang: string) => void
  editFields: Record<ContentFieldKey, string>
  inheritanceMap: Record<ContentFieldKey, 'inherited' | 'overridden'>
  overrideCount: number
  onFieldChange: (key: ContentFieldKey, value: string) => void
  onToggleInherit: (key: ContentFieldKey) => void
  suggestion?: SuggestionMap
  bulletsSlot?: ReactNode
}

export function ChannelEditorPanel({
  productId, channelId, channelName, isMaster, translateMode, translateSourceDesc, translateSourceLang,
  targetLang, availableLanguages, onTargetLangChange, editFields, inheritanceMap,
  overrideCount, masterDescription, onFieldChange, onToggleInherit,
  suggestion, bulletsSlot,
}: ChannelEditorPanelProps) {
  const { t } = useTranslation()
  const showSuggestion = !!suggestion
  const showTranslate = translateMode && !isMaster && !showSuggestion

  const seoFields = SEO_FIELDS.map((key) => ({
    key, value: editFields[key], masterValue: masterDescription?.[key] ?? '',
    isInherited: inheritanceMap[key] === 'inherited',
  }))
  const allSeoInherited = seoFields.every((f) => f.isInherited)

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ChannelEditorHeader channelId={channelId} channelName={channelName}
        isMaster={isMaster} overrideCount={overrideCount} />

      {showSuggestion && suggestion ? (
        <div className="px-5 py-4 space-y-5 overflow-y-auto flex-1">
          {MAIN_FIELDS.map((key) => (
            <div key={key}>
              <div className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 mb-2">
                {t(FIELD_LABELS[key])}
              </div>
              <SuggestionFieldColumn fieldLabel={t(FIELD_LABELS[key])}
                current={editFields[key]} suggestion={suggestion.fields[key] ?? null}
                multiline={MULTILINE_FIELDS.has(key)} rows={key === 'Description' ? 8 : 2}
                onApply={() => suggestion.onApplyField(key)} />
            </div>
          ))}
          <ChannelEditorSeoSection fields={seoFields} isMasterEditor={isMaster}
            allInherited={allSeoInherited} productId={productId} channelId={channelId}
            onChange={onFieldChange} onToggleInherit={onToggleInherit} />
          {bulletsSlot}
        </div>
      ) : (
        <div className={`grid flex-1 overflow-hidden ${showTranslate ? 'grid-cols-2' : 'grid-cols-1'}`}
          style={{ transition: 'grid-template-columns 0.3s ease' }}>
          {showTranslate && (
            <ChannelTranslateColumn description={translateSourceDesc} sourceLang={translateSourceLang} />
          )}
          <div className="flex flex-col min-h-0">
            {showTranslate && (
              <div className="sticky top-0 z-[5] px-4 py-2 border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm">
                <div className="flex items-center gap-1.5">
                  <span className="text-2xs font-medium text-neutral-400 uppercase tracking-wider">
                    {t('productContent.channels.targetLabel')}
                  </span>
                  <select value={targetLang} onChange={(e) => onTargetLangChange(e.target.value)}
                    className="text-2xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700 focus:outline-none">
                    {availableLanguages.map((lang) => (
                      <option key={lang} value={lang}>
                        {LANGUAGE_LABELS[lang] ?? lang.toUpperCase()} — {lang}
                      </option>
                    ))}
                  </select>
                  <span className="text-2xs text-neutral-600">· {t('productContent.channels.editableLabel')}</span>
                </div>
              </div>
            )}
            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
              {MAIN_FIELDS.map((key) => (
                <ChannelEditorField key={key} label={key}
                  value={editFields[key]} masterValue={masterDescription?.[key] ?? ''}
                  isInherited={inheritanceMap[key] === 'inherited'} isMasterEditor={isMaster}
                  multiline={MULTILINE_FIELDS.has(key)}
                  rows={key === 'Description' ? 8 : 2}
                  productId={productId} channelId={channelId}
                  onChange={(v) => onFieldChange(key, v)}
                  onToggleInherit={() => onToggleInherit(key)} />
              ))}
              <ChannelEditorSeoSection fields={seoFields} isMasterEditor={isMaster}
                allInherited={allSeoInherited} productId={productId} channelId={channelId}
                onChange={onFieldChange} onToggleInherit={onToggleInherit} />
              {bulletsSlot}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
